// Cricket Technical Articles and Knowledge Base

export const cricketArticles = [
  {
    id: 1,
    title: "The Science of Bat Speed: Maximizing Power Through Technique",
    category: "Batting Technique",
    difficulty: "Advanced",
    readTime: "12 min",
    image: "https://images.unsplash.com/photo-1531415074968-036ba1b575da?w=600&h=400&fit=crop",
    excerpt: "Understanding the biomechanics behind bat speed and how professional cricketers generate maximum power through proper technique.",
    content: `The science of bat speed is rooted in biomechanics and physics. Elite batsmen like Virat Kohli and Rohit Sharma generate bat speeds exceeding 100 mph through a combination of factors:

**1. Kinetic Chain Activation**
Power generation starts from the ground up. The sequence is: feet → legs → hips → core → shoulders → arms → wrists → bat. Any break in this chain reduces power output by up to 40%.

**2. Bat Weight and Sweet Spot**
The ideal bat weight creates optimal momentum (mass × velocity). Too heavy reduces bat speed; too light reduces momentum. The sweet spot, located 6-8 inches from the toe, provides maximum energy transfer.

**3. Grip Pressure**
Maintain 60% grip pressure at setup, reducing to 40% during backswing, then explosive 100% at impact. This creates the 'whip effect' essential for power hitting.

**4. Follow-Through Physics**
A complete follow-through ensures maximum acceleration through the ball, not to the ball. This adds 15-20% more power to shots.`
  },
  {
    id: 2,
    title: "Understanding Swing Bowling: Conventional vs Reverse",
    category: "Bowling Technique",
    difficulty: "Advanced",
    readTime: "15 min",
    image: "https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=600&h=400&fit=crop",
    excerpt: "A deep dive into the aerodynamics of swing bowling and how to master both conventional and reverse swing.",
    content: `Swing bowling is one of cricket's most fascinating phenomena, governed by fluid dynamics and the Magnus effect.

**Conventional Swing**
Occurs with a new ball (0-20 overs). The shiny side creates laminar airflow while the rough side creates turbulent flow, causing pressure differential and lateral movement.

**Key Factors:**
- Seam position: 20° angle towards slip
- Release: Seam upright, wrist behind the ball
- Ball condition: One side polished, one side rough
- Optimal speed: 75-85 mph for maximum swing

**Reverse Swing**
Occurs with an old ball (40+ overs). Counter-intuitively, the ball swings towards the shiny side due to:
- Turbulent boundary layer separation on the rough side
- Earlier transition on shiny side at high speeds
- Critical speed threshold: 85+ mph required

**Mastering the Wobble Seam**
The modern variation where the seam wobbles in flight, creating unpredictable movement off the pitch. Bowl at 80-82 mph with fingers across the seam.`
  },
  {
    id: 3,
    title: "Footwork Fundamentals: The Foundation of Great Batting",
    category: "Batting Technique",
    difficulty: "Intermediate",
    readTime: "10 min",
    image: "https://images.unsplash.com/photo-1624526267942-ab0ff8a3e972?w=600&h=400&fit=crop",
    excerpt: "Master the essential footwork patterns that separate club cricketers from professionals.",
    content: `Footwork is the foundation upon which all batting technique is built. Without proper foot movement, even the best hand-eye coordination becomes ineffective.

**The Three Key Movements:**

**1. Forward Press**
- Initial trigger movement as bowler releases
- Weight transfers to front foot (60-40 distribution)
- Head moves forward 6-8 inches
- Creates momentum for front-foot shots

**2. Back and Across**
- For short-pitched bowling
- Back foot moves towards off stump
- Weight on balls of feet
- Head remains level and still

**3. The Stride**
- Front foot reaches to pitch of ball
- Stride length: 2.5-3 feet for full deliveries
- Knee bend at 45° angle
- Head over front knee at contact

**Common Errors:**
- Planting front foot before reading length
- Falling over to off side
- Head falling away from the ball
- Rigid knees reducing mobility`
  },
  {
    id: 4,
    title: "Spin Bowling Mastery: Revolutions, Drift and Dip",
    category: "Bowling Technique",
    difficulty: "Expert",
    readTime: "18 min",
    image: "https://images.unsplash.com/photo-1580674684081-7617fbf3d745?w=600&h=400&fit=crop",
    excerpt: "The complete technical guide to becoming an elite spin bowler with insights from international coaching.",
    content: `Elite spin bowling combines physics, biomechanics, and deception. This guide covers the technical aspects that create world-class spinners.

**Revolution Rate**
The key metric for spin bowling effectiveness:
- Club level: 1,200-1,500 RPM
- First-class: 1,800-2,200 RPM
- International elite: 2,400-3,000 RPM

**Creating Drift**
Drift occurs due to the Magnus effect:
- Off-spinner: Ball drifts into right-hander
- Leg-spinner: Ball drifts away from right-hander
- Requires: High revolution rate + optimal seam position + correct trajectory

**Generating Dip**
Dip makes the ball drop sharply before pitching:
- Bowl with more overspin (top-spin)
- Higher release point
- Flight the ball above batsman's eyeline
- Creates uncertainty in footwork

**The Art of Variation**
- Arm ball: No spin, uses same action
- Carrom ball: Flicked with middle finger
- Googly: Leg-spinner's wrong'un
- Doosra: Off-spinner's away turner (controversial)

**Finger Position for Off-Spin:**
Index and middle finger spread across seam, third finger provides stability, spin generated by index finger cutting across the ball.`
  },
  {
    id: 5,
    title: "Cricket Bat Selection: Willow Grades, Weight Distribution & Performance",
    category: "Equipment",
    difficulty: "Intermediate",
    readTime: "14 min",
    image: "https://images.unsplash.com/photo-1531415074968-036ba1b575da?w=600&h=400&fit=crop",
    excerpt: "How to choose the perfect cricket bat based on willow grade, weight, and your playing style.",
    content: `Selecting the right cricket bat is crucial for performance. Understanding the technical aspects helps make informed decisions.

**Willow Grades Explained:**

**Grade 1+ (Premium)**
- 6-12 straight grains
- Minimal blemishes
- Best ping and performance
- Price: ₹25,000-₹60,000+

**Grade 1**
- 5-8 straight grains
- Minor blemishes acceptable
- Excellent performance
- Price: ₹15,000-₹30,000

**Grade 2**
- 4-6 grains (may be irregular)
- Some color variation
- Good club-level performance
- Price: ₹8,000-₹18,000

**Grade 3-4**
- Fewer grains, more color
- Suitable for training/nets
- Price: ₹3,000-₹10,000

**Weight Distribution:**
- **Low middle:** Better for driving, front-foot players
- **Mid-middle:** All-round balance
- **High middle:** Better for pulling/cutting, back-foot players

**Ideal Weight by Role:**
- Opening batsmen: 2lb 8oz - 2lb 10oz (lighter for quick hands)
- Middle order: 2lb 10oz - 2lb 12oz (balanced)
- Power hitters: 2lb 11oz - 2lb 14oz (heavier for power)

**Bat Preparation:**
1. Knocking-in: 6-8 hours minimum
2. Oiling: Raw linseed oil, 2-3 coats
3. Edge protection: Anti-scuff sheet recommended`
  },
  {
    id: 6,
    title: "Wicketkeeping: Stance, Footwork and Taking Spin",
    category: "Wicketkeeping",
    difficulty: "Advanced",
    readTime: "11 min",
    image: "https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=600&h=400&fit=crop",
    excerpt: "Technical guide to wicketkeeping covering stance, movement patterns, and the art of keeping to spin bowling.",
    content: `Wicketkeeping is the most specialized fielding position, requiring unique technical skills and exceptional concentration.

**The Stance:**
- Feet shoulder-width apart
- Weight on balls of feet
- Knees bent at 90-100°
- Head level with bails
- Gloves in front of pads, fingers pointing down

**Standing Up to Spin:**
The true test of a wicketkeeper's skill:

**Positioning:**
- Inner edge of gloves in line with off stump
- Rise as bowler releases
- Watch the ball onto the gloves

**Taking Leg-Spin:**
- Anticipate turn away from right-hander
- Move feet towards first slip region
- Soft hands, give with the ball

**Taking Off-Spin:**
- Ball turns into right-hander
- Feet move towards leg side
- Cover the stumps with body

**Standing Back to Pace:**
- Distance: 18-22 yards from stumps
- Lateral movement priority
- Attack the ball, don't wait
- Dive only as last resort

**Common Errors:**
- Head moving before ball is bowled
- Hard hands causing drops
- Over-anticipating movement
- Standing too close/far from stumps`
  },
  {
    id: 7,
    title: "Playing Fast Bowling: Technique Against 140+ KPH",
    category: "Batting Technique",
    difficulty: "Expert",
    readTime: "16 min",
    image: "https://images.unsplash.com/photo-1624526267942-ab0ff8a3e972?w=600&h=400&fit=crop",
    excerpt: "How to technically prepare for and face genuine fast bowling at international pace.",
    content: `Facing bowling at 140+ kph requires specific technical adjustments and mental preparation.

**The Physics of Reaction Time:**
At 150 kph, the ball reaches the batsman in approximately 0.4 seconds. Human reaction time is 0.2 seconds, leaving only 0.2 seconds for shot execution.

**Key Technical Adjustments:**

**1. Backlift Modification**
- Reduce backlift height by 30%
- Start with bat higher in stance
- Eliminates unnecessary movement
- Bat face stays closer to ball path

**2. Guard Position**
- Consider middle or middle-and-leg
- Slight leg-side bias for swing
- Reduces lbw vulnerability

**3. Trigger Movements**
- Smaller, quicker triggers
- Back-and-across becomes essential
- Minimal forward press (avoid overcommitment)

**4. Shot Selection**
- Work within V initially (mid-on to mid-off)
- Leave more deliveries outside off
- Punches over drives
- Pull shot for anything short

**5. Eye Focus**
- Watch the release point intensely
- Pick up length from bowler's hand
- "See it early" mantra

**Mental Approach:**
- First 10 balls: survival priority
- Accept discomfort as normal
- Trust your technique
- Don't chase width early`
  },
  {
    id: 8,
    title: "Field Placement Strategy: Captaincy Masterclass",
    category: "Strategy",
    difficulty: "Advanced",
    readTime: "13 min",
    image: "https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=600&h=400&fit=crop",
    excerpt: "Understanding field placements, bowling plans, and how to think like an international captain.",
    content: `Field placement is chess on grass. Great captains think three moves ahead, setting traps and creating pressure.

**Fundamental Principles:**

**1. Bowling Partnership**
- Field to the bowler's strength
- Create dot ball pressure
- Protect against bad balls

**2. Batsman Analysis**
- Study wagon wheels pre-match
- Identify scoring zones
- Know their weaknesses

**Attacking Fields for Pace:**

**New Ball (Overs 1-10):**
- 3 slips + gully for swing
- Fine leg for bad balls
- No third man (encourages drives)
- Short leg for newcomers

**Middle Overs Pressure:**
- 6-3 off-side field
- Cut and cover
- Deep point, sweeper cover
- Aim for 4-5 RPO

**Spin Bowling Fields:**

**Off-Spin to Right-Hander:**
- Slip + leg slip for turn
- Short leg for bat-pad
- Deep mid-wicket for sweep
- Forward short leg for the push

**Leg-Spin to Right-Hander:**
- Slip + leg slip
- Deep square leg for sweep
- Cover for the drive
- Backward point for cut

**Death Overs Strategy:**
- Long-on, long-off non-negotiable
- Third man, fine leg on boundary
- Yorker length priority
- Change bowler if pattern readable`
  },
  {
    id: 9,
    title: "Power Hitting in T20: Biomechanics of the Modern Slog",
    category: "Batting Technique",
    difficulty: "Expert",
    readTime: "14 min",
    image: "https://images.unsplash.com/photo-1531415074968-036ba1b575da?w=600&h=400&fit=crop",
    excerpt: "The science behind T20 power hitting and how to develop devastating boundary-hitting ability.",
    content: `T20 cricket has revolutionized batting technique. Power hitting is now a science combining biomechanics, strength, and skill.

**The Biomechanics of Power:**

**1. X-Factor Stretch**
The separation between hip and shoulder rotation during the swing:
- Amateur: 20-30°
- Professional: 40-50°
- Elite power hitters: 50-60°

Greater X-factor = more stored elastic energy = more power

**2. Bat Path**
Modern power hitters use an uppercut swing:
- Traditional: Level bat path
- T20: 10-15° upward angle
- Launch angle for maximum: 25-35°

**3. Clearing the Front Leg**
- Hip opens early
- Front leg straightens and rotates
- Creates room for bat swing
- See: ABD, Pollard, Russell

**Key Power Shots:**

**The Slog Sweep:**
- Get under the ball
- Target: Deep mid-wicket to cow corner
- Risk: Top edge, bowled
- Practice: 500 balls per week minimum

**The Helicopter Shot:**
- Made famous by MS Dhoni
- Late wrist rotation
- Hits yorker length for six
- Requires exceptional wrist strength

**The Scoop/Ramp:**
- Uses bowler's pace
- Minimal bat swing required
- Target: Fine leg boundary
- Timing over power

**Strength Training for Power:**
- Rotational medicine ball throws
- Cable woodchops
- Weighted bat swings
- Hip rotation exercises`
  },
  {
    id: 10,
    title: "Fitness & Conditioning for Cricket: The Complete Guide",
    category: "Fitness",
    difficulty: "Intermediate",
    readTime: "20 min",
    image: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=600&h=400&fit=crop",
    excerpt: "Position-specific fitness requirements and training programs for aspiring professional cricketers.",
    content: `Modern cricket demands exceptional fitness. This guide covers position-specific requirements and training protocols.

**Physical Demands by Position:**

**Fast Bowlers:**
- Highest workload position
- 2,000-3,000 meters per session
- Multiple high-intensity sprints
- Huge spinal loading (8-10x body weight)

**Spinners:**
- Lower intensity, high volume
- Core stability essential
- Finger/wrist strength critical
- Flexibility for action

**Batsmen:**
- Explosive power needed
- Running between wickets (2s and 3s)
- Concentration stamina
- Core strength for shot-making

**Wicketkeepers:**
- Constant squatting
- Lateral movement
- Hand-eye coordination
- Mental concentration

**Training Program Framework:**

**Pre-Season (8-12 weeks):**
- Aerobic base building
- Strength development
- Injury prevention work
- Technical refinement

**In-Season:**
- Maintenance training
- Recovery priority
- Match-specific preparation
- Load management

**Key Exercises:**

**Lower Body:**
- Squats: 3x5 @ 80%
- Romanian Deadlifts: 3x8
- Single leg work: 3x10 each
- Plyometrics: Box jumps, bounds

**Core:**
- Anti-rotation holds
- Medicine ball rotations
- Pallof press: 3x12
- Dead bugs: 3x15

**Recovery Protocols:**
- Ice baths post-match
- Compression garments
- Sleep: 8-9 hours minimum
- Hydration: 3-4 liters daily`
  },
  {
    id: 11,
    title: "Mental Game: Psychology of Elite Cricket Performance",
    category: "Mental Skills",
    difficulty: "Advanced",
    readTime: "17 min",
    image: "https://images.unsplash.com/photo-1594470117722-de4b9a02ebed?w=600&h=400&fit=crop",
    excerpt: "Sports psychology techniques used by international cricketers to perform under pressure.",
    content: `The mental aspect separates good cricketers from great ones. This guide covers proven psychological techniques.

**The Pressure Performance Model:**

Pressure = Importance × Uncertainty

Reducing either factor improves performance under pressure.

**Pre-Performance Routines:**

**Batting:**
1. Visualize first 10 balls
2. Repeat technical cue word
3. Controlled breathing (4-4-4)
4. Take guard, look at fielders
5. Focus on bowler's hand

**Bowling:**
1. Visualize delivery landing
2. Feel the seam position
3. Deep breath at top of mark
4. Focus word on approach
5. Trust the process

**Dealing with Failure:**

**The 4 R's Protocol:**
1. **Recognize** the emotion
2. **Release** it (breath/movement)
3. **Refocus** on next ball
4. **Recommit** to your process

**Concentration Techniques:**

**Ball-by-Ball Focus:**
- See each ball as the first ball
- "This ball is the only ball"
- Park previous deliveries immediately

**External Focus:**
- Focus on ball, not technique
- Watch seam rotation
- Trust muscle memory

**Visualization Practice:**
- Daily 10-minute sessions
- Use all senses
- Include challenging scenarios
- Always end successfully

**Building Confidence:**
- Recall past successes
- Prepare thoroughly
- Positive self-talk
- Body language awareness

**Resources:**
Visit [Breatheflow.live](https://breatheflow.live) for guided breathing and mindfulness exercises specifically designed for athletes.`
  },
  {
    id: 12,
    title: "Death Bowling Masterclass: Yorkers, Slower Balls & Variations",
    category: "Bowling Technique",
    difficulty: "Expert",
    readTime: "15 min",
    image: "https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=600&h=400&fit=crop",
    excerpt: "Complete technical guide to bowling in the death overs with all modern variations explained.",
    content: `Death bowling is the most high-pressure skill in T20 cricket. This guide covers the technical aspects of all variations.

**The Yorker:**

**Types:**
1. **Straight yorker:** At the base of middle stump
2. **Wide yorker:** Outside off, hard to hit straight
3. **Toe-crusher:** At the batsman's feet
4. **Slot yorker:** Wider, invites the hit

**Technical Keys:**
- Release point: slightly earlier than length ball
- Follow through towards target
- Front arm pulls down hard
- Head stays level and still

**Practice Method:**
- Place a shoe at the crease
- 100 yorkers daily minimum
- Vary pace: 85%, 90%, 100%

**Slower Ball Variations:**

**1. Back of Hand Slower:**
- Palm faces batsman at release
- 15-20 kph slower
- Hardest to detect

**2. Knuckle Ball:**
- Pushed with knuckles
- No seam rotation
- Dips and wobbles
- 10-15 kph reduction

**3. Slower Bouncer:**
- Released like bouncer
- 15-20 kph slower
- Deceives pull shot

**4. Off-Cutter:**
- Fingers cut across seam
- Moves away from right-hander
- Use with yorker field

**5. Leg-Cutter:**
- Fingers roll over seam
- Moves into right-hander
- Effective with straight field

**Reading the Batsman:**

**Pre-Delivery Cues:**
- Grip position on bat
- Stance width changes
- Eye movement patterns
- Weight distribution

**Counter-Strategies:**
- Slog sweep setup → Wide yorker
- Advance anticipated → Slower bouncer
- Back foot movement → Full and fast
- Open stance → Target pads`
  }
];

export const articleCategories = [
  "All Articles",
  "Batting Technique",
  "Bowling Technique",
  "Wicketkeeping",
  "Equipment",
  "Fitness",
  "Mental Skills",
  "Strategy"
];
